import React, { useState, useEffect, useRef, Suspense, lazy, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { NodeGroupName, NodeStatus } from '../types';
import { nodeGroupAPI } from '../services/api';
import { getNodeGroupNamesFromAutoscaleResponse, normalizeAutoscaleToNodeStatus } from '../utils/autoscaleNodeState';
import { isDevelopmentEnv } from '../config/runtimeConfig';
import { Plus, Eye, X, RefreshCw, Server, Video, Code, Copy, Check, Radio, Settings, Layers, Repeat, ArrowLeft, ChevronDown } from 'lucide-react';

const NodeGroupStatus = lazy(() => import('./NodeGroupStatus'));

interface NodeGroupManagementProps {
  /** Called when switching between list view and Node Group Details view. */
  onViewChange?: (view: 'list' | 'status') => void;
  /** When set (e.g. after Edit Node Group), open Node Group Details for this group. */
  initialSelectedGroupForStatus?: NodeGroupName | null;
}

interface StreamInfo {
  streamGuid: string;
  serverAddress: string;
  nodeRole: string;
  subGroup: string;
  nodeState: string;
  subscribers: number;
  dataClientCount: number;
  durationMs: number;
  state: string;
  streamSource: string;
  statType: string;
}

const NodeGroupManagement: React.FC<NodeGroupManagementProps> = ({ onViewChange, initialSelectedGroupForStatus }) => {
  const navigate = useNavigate();
  const [nodeGroupNames, setNodeGroupNames] = useState<NodeGroupName[]>([]);
  /** Active node group names from GET /as/v1/admin/nodegroup; others are "destroying" (only in autoscale). */
  const [activeNodeGroupNames, setActiveNodeGroupNames] = useState<Set<string>>(new Set());
  const [groupStatuses, setGroupStatuses] = useState<{ [groupName: string]: NodeStatus[] }>({});
  const [groupStreams, setGroupStreams] = useState<{ [groupName: string]: StreamInfo[] }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedGroupForStatus, setSelectedGroupForStatus] = useState<NodeGroupName | null>(null);

  // When returning from Edit Node Group, open Node Group Details for the updated group
  useEffect(() => {
    if (initialSelectedGroupForStatus) {
      setSelectedGroupForStatus(initialSelectedGroupForStatus);
    }
  }, [initialSelectedGroupForStatus]);

  // Create Node Group modal: copy from existing or create new
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [copyFromGroupName, setCopyFromGroupName] = useState<NodeGroupName | ''>('');
  const createModalBackdropMousedownRef = useRef(false);

  // Notify parent when switching between list and Node Group Details view
  React.useEffect(() => {
    onViewChange?.(selectedGroupForStatus ? 'status' : 'list');
  }, [selectedGroupForStatus, onViewChange]);
  
  // JSON Config state
  const [showJsonConfig, setShowJsonConfig] = useState(false);
  const [selectedGroupForJson, setSelectedGroupForJson] = useState<NodeGroupName | null>(null);
  const [jsonConfig, setJsonConfig] = useState<any>(null);
  const [jsonLoading, setJsonLoading] = useState(false);
  const [jsonError, setJsonError] = useState('');
  const [jsonConfigCopied, setJsonConfigCopied] = useState(false);

  // Auto-refresh: 0 = off, else interval in seconds (default 15s)
  const [autoRefreshInterval, setAutoRefreshInterval] = useState<number>(15);
  const [autoRefreshDropdownOpen, setAutoRefreshDropdownOpen] = useState(false);
  const autoRefreshDropdownRef = useRef<HTMLDivElement>(null);

  const fetchNodeGroups = useCallback(async (opts?: { silent?: boolean }) => {
    const silent = opts?.silent === true;
    try {
      if (!silent) setLoading(true);
      setError('');
      // Fetch autoscale node-state and admin nodegroup in parallel
      const [autoscaleData, adminGroupList] = await Promise.all([
        nodeGroupAPI.getAutoscaleNodeState(),
        nodeGroupAPI.getNodeGroups().catch(() => [] as string[]),
      ]);
      const adminNames = Array.isArray(adminGroupList) ? adminGroupList : [];
      setActiveNodeGroupNames(new Set(adminNames));

      // Full list from autoscale (includes both active and destroying)
      const fetchedNodeGroupNames = getNodeGroupNamesFromAutoscaleResponse(autoscaleData);
      setNodeGroupNames(fetchedNodeGroupNames);

      // Derive status for each group from the same autoscale response
      const statuses: { [groupName: string]: NodeStatus[] } = {};
      fetchedNodeGroupNames.forEach((name) => {
        statuses[name] = normalizeAutoscaleToNodeStatus(autoscaleData, name);
      });
      setGroupStatuses(statuses);

      // Fetch streams per group (unchanged)
      const streams: { [groupName: string]: StreamInfo[] } = {};
      await Promise.all(
        fetchedNodeGroupNames.map(async (name) => {
          try {
            const streamsData = await nodeGroupAPI.getNodeGroupStreams(name);
            if (Array.isArray(streamsData)) {
              streams[name] = streamsData.filter(
                (stream: any) => stream && typeof stream.subscribers === 'number'
              );
            } else {
              streams[name] = [];
            }
          } catch {
            streams[name] = [];
          }
        })
      );
      setGroupStreams(streams);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch node groups');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (autoRefreshInterval <= 0) return;
    const id = setInterval(() => fetchNodeGroups({ silent: true }), autoRefreshInterval * 1000);
    return () => clearInterval(id);
  }, [autoRefreshInterval, fetchNodeGroups]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (autoRefreshDropdownRef.current && !autoRefreshDropdownRef.current.contains(e.target as Node)) {
        setAutoRefreshDropdownOpen(false);
      }
    };
    if (autoRefreshDropdownOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [autoRefreshDropdownOpen]);

  useEffect(() => {
    fetchNodeGroups();
  }, [fetchNodeGroups]);

  const activeNodeGroupNamesList = useMemo(
    () => nodeGroupNames.filter((name) => activeNodeGroupNames.has(name)),
    [nodeGroupNames, activeNodeGroupNames]
  );

  // Reserved for delete action when wired to UI
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleDelete = async (groupName: string) => {
    if (!window.confirm(`Are you sure you want to delete the node group "${groupName}"?`)) {
      return;
    }

    try {
      await nodeGroupAPI.deleteNodeGroup(groupName);
      fetchNodeGroups();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete node group');
    }
  };

  const handleViewStatus = (groupName: string) => {
    setSelectedGroupForStatus(groupName);
  };

  const getBaseRole = (role: string): string => {
    if (!role) return '';
    const lower = role.toLowerCase();
    if (lower.includes('origin')) return 'origin';
    if (lower.includes('transcoder')) return 'transcoder';
    if (lower.includes('mixer')) return 'mixer';
    if (lower.includes('relay')) return 'relay';
    if (lower.includes('video_packager')) return 'video_packager';
    if (lower.includes('edge') || lower.startsWith('auto-edge-')) return 'edge';
    return lower;
  };

  // Helper function to count nodes by role with comprehensive error handling
  const countNodesByRole = (groupName: string, role: string) => {
    try {
      const nodes = groupStatuses[groupName];
      if (!Array.isArray(nodes)) return 0;
      return nodes.reduce((count, node) => {
        try {
          if (!node?.nodeEvent?.nodeRoleName) return count;
          return count + (getBaseRole(node.nodeEvent.nodeRoleName) === role ? 1 : 0);
        } catch {
          return count;
        }
      }, 0);
    } catch {
      return 0;
    }
  };

  // Count edge nodes: role contains "edge" (e.g. edge, edge1080, auto-edge-xxx)
  const countEdgeNodes = (groupName: string) => {
    try {
      const nodes = groupStatuses[groupName];
      if (!Array.isArray(nodes)) return 0;
      return nodes.reduce((count, node) => {
        try {
          const role = node?.nodeEvent?.nodeRoleName;
          if (!role) return count;
          return count + (getBaseRole(role) === 'edge' ? 1 : 0);
        } catch {
          return count;
        }
      }, 0);
    } catch {
      return 0;
    }
  };

  // Helper function to count live streams (unique by streamGuid, matching Node Group Details aggregate count)
  const countLiveStreams = (groupName: string) => {
    try {
      const streams = groupStreams[groupName];
      if (!Array.isArray(streams)) return 0;
      const liveGuids = new Set<string>();
      streams.forEach((stream) => {
        if (stream && stream.state === 'LIVE' && stream.streamGuid) {
          liveGuids.add(stream.streamGuid);
        }
      });
      return liveGuids.size;
    } catch {
      return 0;
    }
  };

  // Helper function to count total subscribers with comprehensive error handling
  const countTotalSubscribers = (groupName: string) => {
    try {
      const streams = groupStreams[groupName];
      if (!Array.isArray(streams)) return 0;
      return streams.reduce((sum, stream) => {
        try {
          if (!stream) return sum;
          return sum + (typeof stream.subscribers === 'number' ? stream.subscribers : 0);
        } catch {
          return sum;
        }
      }, 0);
    } catch {
      return 0;
    }
  };

  const fetchJsonConfig = async (groupName: NodeGroupName) => {
    try {
      setJsonLoading(true);
      setJsonError('');
      const data = await nodeGroupAPI.getNodeGroup(groupName);
      setJsonConfig(data);
    } catch (err: any) {
      setJsonError(err.response?.data?.message || 'Failed to fetch node group configuration');
    } finally {
      setJsonLoading(false);
    }
  };

  // Reserved for edit action when wired to UI
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleEdit = (groupName: NodeGroupName) => {
    setSelectedGroupForJson(groupName);
    setShowJsonConfig(true);
    fetchJsonConfig(groupName);
  };

  // If showing node status, render the status component (lazy-loaded)
  if (selectedGroupForStatus) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <button
            onClick={() => setSelectedGroupForStatus(null)}
            className="text-white hover:text-red5-red flex items-center space-x-2 mr-6 transition-colors duration-300"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Back to Dashboard</span>
          </button>
        </div>
        <Suspense
          fallback={
            <div className="flex items-center justify-center py-24">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-red5-red" />
              <span className="ml-3 text-white">Loading node group details...</span>
            </div>
          }
        >
          <NodeGroupStatus
            nodeGroupName={selectedGroupForStatus}
            isGroupActive={activeNodeGroupNames.has(selectedGroupForStatus)}
          />
        </Suspense>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="bg-red5-gradient border border-red5-border rounded-lg shadow-lg p-6">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red5-red"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative bg-red5-gradient border border-red5-border rounded-xl p-8 mb-8 overflow-hidden">
      {/* Create Node Group modal: copy from existing or create new (Publish stream style) */}
      {showCreateModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70 p-4"
          onMouseDown={(e) => { createModalBackdropMousedownRef.current = e.target === e.currentTarget; }}
          onClick={(e) => {
            if (createModalBackdropMousedownRef.current && e.target === e.currentTarget) setShowCreateModal(false);
            createModalBackdropMousedownRef.current = false;
          }}
        >
          <div className="bg-red5-dark rounded-lg shadow-xl max-w-md w-full border border-red5-border overflow-hidden" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 bg-red5-gradient border-b border-red5-border">
              <h3 className="text-lg font-normal text-white">Create Node Group</h3>
              <button
                type="button"
                onClick={() => { setShowCreateModal(false); setCopyFromGroupName(''); }}
                className="text-white opacity-70 hover:opacity-100 hover:text-red5-red transition-colors p-1 focus:outline-none"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <p className="text-sm text-white opacity-80">Create a new node group from scratch or copy configuration from an existing one.</p>
              <button
                type="button"
                onClick={() => { setShowCreateModal(false); navigate('/create-node-group'); }}
                className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-[8px] border border-red5-border bg-red5-red text-white hover:bg-red5-red/90 focus:outline-none transition-colors text-sm"
              >
                <Plus className="h-4 w-4" />
                Create new
              </button>
              {nodeGroupNames.length > 0 && activeNodeGroupNames.size > 0 && (
                <>
                  <div className="flex items-center gap-2 text-sm text-white opacity-80">
                    <span className="flex-shrink-0">or copy from existing (active only):</span>
                  </div>
                  <div className="flex gap-2">
                    <select
                      value={copyFromGroupName}
                      onChange={(e) => setCopyFromGroupName(e.target.value as NodeGroupName)}
                      className="flex-1 px-3 py-2 bg-red5-card-dark border border-red5-border rounded-md text-white text-sm focus:outline-none"
                    >
                      <option value="">Select node group</option>
                      {activeNodeGroupNamesList.map((name) => (
                          <option key={name} value={name}>{name}</option>
                        ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => {
                        if (copyFromGroupName) {
                          setShowCreateModal(false);
                          navigate('/create-node-group', { state: { copyFrom: copyFromGroupName } });
                          setCopyFromGroupName('');
                        }
                      }}
                      disabled={!copyFromGroupName}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-[8px] border border-red5-border bg-red5-card-dark text-white hover:bg-red5-card-hover focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
                    >
                      <Copy className="h-4 w-4" />
                      Continue
                    </button>
                  </div>
                </>
              )}
              <div className="pt-2 border-t border-red5-border">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); setCopyFromGroupName(''); }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-[8px] border border-red5-border bg-red5-card-dark text-white hover:bg-red5-card-hover focus:outline-none transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div className="mb-3">
          <div className="text-2xl font-normal text-white mb-3">Node Groups</div>
          <div className="text-base text-red5-red font-mono opacity-80">Manage your streaming infrastructure</div>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-[8px] border border-red5-border bg-red5-card-dark text-white hover:bg-red5-card-hover transition-colors text-sm"
          >
            <Plus className="h-4 w-4" />
            Create Node Group
          </button>
          <div className="flex items-stretch rounded-[8px] border border-red5-border bg-red5-card-dark" ref={autoRefreshDropdownRef}>
            <button
              onClick={() => fetchNodeGroups({ silent: true })}
              className="flex items-center gap-2 px-4 py-2 text-white hover:bg-red5-card-hover transition-colors text-sm border-r border-red5-border"
              title="Refresh node groups"
            >
              <RefreshCw className="h-4 w-4" />
              Refresh
            </button>
            <div className="relative flex items-center">
              <button
                type="button"
                onClick={() => setAutoRefreshDropdownOpen((v) => !v)}
                className="flex items-center justify-center min-w-[2.5rem] h-full px-2 py-2 text-white hover:bg-red5-card-hover transition-colors text-sm"
              >
                {autoRefreshInterval === 0 ? (
                  <ChevronDown className={`h-4 w-4 transition-transform ${autoRefreshDropdownOpen ? 'rotate-180' : ''}`} />
                ) : (
                  <span className="flex items-center gap-1">
                    <span className="text-xs font-normal">
                      {autoRefreshInterval === 60 ? '1m' : `${autoRefreshInterval}s`}
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${autoRefreshDropdownOpen ? 'rotate-180' : ''}`} />
                  </span>
                )}
              </button>
              {autoRefreshDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 z-50 min-w-[5rem] py-1 rounded border border-red5-border bg-red5-card-dark shadow-lg">
                  {[
                    { sec: 0, label: 'Off' },
                    { sec: 5, label: '5s' },
                    { sec: 10, label: '10s' },
                    { sec: 15, label: '15s' },
                    { sec: 30, label: '30s' },
                    { sec: 60, label: '1m' },
                  ].map(({ sec, label }) => (
                    <button
                      key={sec}
                      type="button"
                      onClick={() => {
                        setAutoRefreshInterval(sec);
                        setAutoRefreshDropdownOpen(false);
                      }}
                      className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                        autoRefreshInterval === sec
                          ? 'bg-red5-green/20 text-red5-green'
                          : 'text-white hover:bg-red5-card-darker'
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mt-4 bg-red5-card-dark border border-red5-red rounded-lg p-4">
          <div className="flex items-center">
            <div className="w-5 h-5 bg-red5-red rounded-full mr-3 flex-shrink-0"></div>
            <div>
              <h3 className="text-sm font-normal text-red5-red">Error</h3>
              <p className="text-sm text-red5-red mt-1">{error}</p>
            </div>
          </div>
        </div>
      )}

      {nodeGroupNames.length === 0 ? (
        <div className="py-12 text-center">
          <div className="max-w-md mx-auto">
            <h3 className="text-lg font-normal text-white mb-2">No node groups found</h3>
            <p className="text-white opacity-70">Create your first node group to get started with your streaming infrastructure.</p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-red5-border" style={{ borderSpacing: '0 0' }}>
              <thead className="bg-red5-card-dark">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-normal text-white uppercase tracking-wider">
                    <div className="flex items-center space-x-2">
                      <Server className="h-4 w-4 text-red5-red" />
                      <span>Node Group</span>
                    </div>
                  </th>
                  {/* Node Roles Section */}
                  <th className="px-3 py-3 text-center text-xs font-normal text-white uppercase tracking-wider border-l-2 border-red5-border">
                    <div className="flex flex-col items-center justify-center gap-0.5">
                      <Video className="h-4 w-4 text-red-500" />
                      <span>Origin</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-normal text-white uppercase tracking-wider">
                    <div className="flex flex-col items-center justify-center gap-0.5">
                      <Radio className="h-4 w-4 text-blue-500" />
                      <span>Edge</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-normal text-white uppercase tracking-wider">
                    <div className="flex flex-col items-center justify-center gap-0.5">
                      <Settings className="h-4 w-4 text-red5-green" />
                      <span>Transcoder</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-normal text-white uppercase tracking-wider">
                    <div className="flex flex-col items-center justify-center gap-0.5">
                      <Layers className="h-4 w-4 text-purple-500" />
                      <span>Mixer</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-normal text-white uppercase tracking-wider">
                    <div className="flex flex-col items-center justify-center gap-0.5">
                      <Repeat className="h-4 w-4 text-red5-gold" />
                      <span>Relay</span>
                    </div>
                  </th>
                  <th className="px-3 py-3 text-center text-xs font-normal text-white uppercase tracking-wider">
                    <div className="flex flex-col items-center justify-center gap-0.5">
                      <Copy className="h-4 w-4 text-teal-500" />
                      <span>Video Packager</span>
                    </div>
                  </th>
                  {/* Streams Section - Closer together */}
                  <th className="px-1 py-3 text-center text-xs font-normal text-white uppercase tracking-wider border-l-2 border-red5-border bg-red5-card-dark">
                    <div className="flex items-center justify-center space-x-1">
                      <Video className="h-3 w-3 text-red5-red" />
                      <span>Streams</span>
                    </div>
                  </th>
                  <th className="px-1 py-3 text-center text-xs font-normal text-white uppercase tracking-wider bg-red5-card-dark" style={{ paddingLeft: '2px' }}>
                    <div className="flex items-center justify-center space-x-1">
                      <Eye className="h-3 w-3 text-red5-red" />
                      <span>Subscribers</span>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-red5-card-dark divide-y divide-red5-border">
                {nodeGroupNames.map((groupName, index) => {
                  // Safe calculation of counts
                  const originCount = (() => {
                    try {
                      return countNodesByRole(groupName, 'origin');
                    } catch (error) {
                      if (isDevelopmentEnv()) console.error(`Error counting origins for ${groupName}:`, error);
                      return 0;
                    }
                  })();
                  
                  const edgeCount = (() => {
                    try {
                      return countEdgeNodes(groupName);
                    } catch (error) {
                      if (isDevelopmentEnv()) console.error(`Error counting edges for ${groupName}:`, error);
                      return 0;
                    }
                  })();
                  
                  const transcoderCount = (() => {
                    try {
                      return countNodesByRole(groupName, 'transcoder');
                    } catch (error) {
                      if (isDevelopmentEnv()) console.error(`Error counting transcoders for ${groupName}:`, error);
                      return 0;
                    }
                  })();
                  
                  const mixerCount = (() => {
                    try {
                      return countNodesByRole(groupName, 'mixer');
                    } catch (error) {
                      if (isDevelopmentEnv()) console.error(`Error counting mixers for ${groupName}:`, error);
                      return 0;
                    }
                  })();
                  
                  const relayCount = (() => {
                    try {
                      return countNodesByRole(groupName, 'relay');
                    } catch (error) {
                      if (isDevelopmentEnv()) console.error(`Error counting relays for ${groupName}:`, error);
                      return 0;
                    }
                  })();
                  
                  const videoPackagerCount = (() => {
                    try {
                      return countNodesByRole(groupName, 'video_packager');
                    } catch (error) {
                      if (isDevelopmentEnv()) console.error(`Error counting video packagers for ${groupName}:`, error);
                      return 0;
                    }
                  })();
                  
                  const liveStreamsCount = (() => {
                    try {
                      return countLiveStreams(groupName);
                    } catch (error) {
                      if (isDevelopmentEnv()) console.error(`Error counting live streams for ${groupName}:`, error);
                      return 0;
                    }
                  })();
                  
                  const subscribersCount = (() => {
                    try {
                      return countTotalSubscribers(groupName);
                    } catch {
                      return 0;
                    }
                  })();

                  return (
                    <tr 
                      key={groupName} 
                      onClick={() => handleViewStatus(groupName)}
                      className="hover:bg-red5-card-darker transition-all duration-200 group cursor-pointer"
                    >
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center space-x-2">
                          {(() => {
                            const isActive = activeNodeGroupNames.has(groupName);
                            const nodes = groupStatuses[groupName];
                            const hasNonInService = Array.isArray(nodes) && nodes.some(
                              (n) => n?.scalingEvent?.state !== 'INSERVICE'
                            );
                            const iconColor = !isActive
                              ? 'bg-red-500'
                              : hasNonInService
                                ? 'bg-red5-gold'
                                : 'bg-red5-green';
                            const iconTitle = !isActive
                              ? 'Destroying'
                              : hasNonInService
                                ? 'Active (some nodes not INSERVICE)'
                                : 'Active';
                            return (
                              <div
                                className={`w-6 h-6 rounded-lg flex items-center justify-center shrink-0 ${iconColor}`}
                                title={iconTitle}
                              >
                                <span className="text-white text-xs font-normal">
                                  {groupName.charAt(0).toUpperCase()}
                                </span>
                              </div>
                            );
                          })()}
                          <div>
                            <div className="text-sm font-normal text-white group-hover:text-red5-red transition-colors">
                              {groupName}
                            </div>
                            <div className="text-xs text-white opacity-70">
                              #{index + 1}
                            </div>
                          </div>
                        </div>
                      </td>
                      {/* Node Roles Section - show value only when > 0, no circles, slightly larger text */}
                      <td className="px-3 py-3 whitespace-nowrap text-center border-l-2 border-red5-border">
                        {originCount > 0 ? (
                          <span className="text-sm font-normal text-red-500">{originCount}</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-center">
                        {edgeCount > 0 ? (
                          <span className="text-sm font-normal text-blue-400">{edgeCount}</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-center">
                        {transcoderCount > 0 ? (
                          <span className="text-sm font-normal text-red5-green">{transcoderCount}</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-center">
                        {mixerCount > 0 ? (
                          <span className="text-sm font-normal text-purple-400">{mixerCount}</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-center">
                        {relayCount > 0 ? (
                          <span className="text-sm font-normal text-red5-gold-light">{relayCount}</span>
                        ) : null}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-center">
                        {videoPackagerCount > 0 ? (
                          <span className="text-sm font-normal text-teal-400">{videoPackagerCount}</span>
                        ) : null}
                      </td>
                      {/* Streams Section - always show value (including 0); no bg so row hover illuminates full line */}
                      <td className="px-1 py-3 whitespace-nowrap text-center border-l-2 border-red5-border">
                        <span className="text-sm font-normal text-red-400">{liveStreamsCount}</span>
                      </td>
                      <td className="py-3 whitespace-nowrap text-center" style={{ paddingLeft: '2px', paddingRight: '2px' }}>
                        <span className="text-sm font-normal text-blue-400">{subscribersCount}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* JSON Configuration Modal */}
      {showJsonConfig && selectedGroupForJson && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-red5-dark rounded-lg shadow-xl max-w-6xl w-full h-[90vh] flex flex-col border border-red5-border">
            {/* Header */}
            <div className="flex items-center justify-between p-4 bg-red5-gradient border-b border-red5-border flex-shrink-0">
              <div className="flex items-center space-x-2">
                <Code className="h-5 w-5 text-red5-red" />
                <h3 className="text-lg font-normal text-white">
                  Node Group Configuration: {selectedGroupForJson}
                </h3>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-red5-green rounded-full"></div>
                  <span className="text-xs text-white opacity-70">Live JSON</span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    if (jsonConfig) {
                      navigator.clipboard.writeText(JSON.stringify(jsonConfig, null, 2));
                      setJsonConfigCopied(true);
                      setTimeout(() => setJsonConfigCopied(false), 2000);
                    }
                  }}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[6px] border text-xs shrink-0 transition-colors ${jsonConfigCopied ? 'border-red5-green bg-red5-green/20 text-red5-green' : 'border-red5-border bg-red5-card-dark text-white hover:bg-red5-card-hover'}`}
                >
                  {jsonConfigCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {jsonConfigCopied ? 'Copied' : 'Copy JSON'}
                </button>
                <button
                  onClick={() => {
                    setShowJsonConfig(false);
                    setSelectedGroupForJson(null);
                    setJsonConfig(null);
                    setJsonConfigCopied(false);
                  }}
                  className="text-white opacity-70 hover:opacity-100 hover:text-red5-red transition-colors p-1"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
            </div>
            
            {/* Content Area */}
            <div className="flex-1 overflow-hidden bg-gray-900">
              {jsonLoading ? (
                <div className="flex items-center justify-center h-full bg-gray-900">
                  <div className="flex items-center space-x-3">
                    <RefreshCw className="animate-spin h-6 w-6 text-red5-red" />
                    <span className="text-white">Loading configuration...</span>
                  </div>
                </div>
              ) : jsonError ? (
                <div className="p-6 h-full overflow-auto bg-gray-900">
                  <div className="bg-red5-card-dark border border-red5-red rounded-lg p-4">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-red5-red rounded-full mr-3"></div>
                      <div>
                        <h3 className="text-sm font-normal text-red5-red">Error Loading Configuration</h3>
                        <p className="text-sm text-red5-red mt-1">{jsonError}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => fetchJsonConfig(selectedGroupForJson)}
                      className="flex items-center gap-2 mt-4 px-4 py-2 rounded-[8px] border border-red5-border bg-red5-red text-white hover:bg-red5-red/90 transition-colors text-sm"
                    >
                      Retry
                    </button>
                  </div>
                </div>
              ) : jsonConfig ? (
                <div className="h-full flex flex-col bg-gray-900">
                  {/* JSON Display */}
                  <div className="flex-1 overflow-auto bg-gray-900" style={{
                    scrollbarWidth: 'thin',
                    scrollbarColor: 'var(--red5-border) var(--red5-card-dark)'
                  }}>
                    <pre 
                      className="p-4 text-red5-green font-mono text-sm whitespace-pre-wrap bg-gray-900" 
                      style={{ 
                        lineHeight: '1.5'
                      }}
                    >
                      {JSON.stringify(jsonConfig, null, 2)}
                    </pre>
                  </div>
                </div>
              ) : null}
            </div>
            
            {/* Footer */}
            {jsonConfig && (
              <div className="p-3 bg-red5-gradient border-t border-red5-border flex-shrink-0">
                <div className="flex items-center justify-between text-xs text-white opacity-70">
                  <div className="flex items-center space-x-4">
                    <span>JSON Size: {JSON.stringify(jsonConfig).length} characters</span>
                    <span>Lines: {JSON.stringify(jsonConfig, null, 2).split('\n').length}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span>Configuration loaded</span>
                    <div className="w-2 h-2 bg-red5-green rounded-full"></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NodeGroupManagement; 